import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Spin, notification, Modal, Button } from "antd";
import ChartContainer from "modules/shared/PickupDropoff/chart/ChartContainer";
import MapBox from "modules/shared/PickupDropoff/mapbox/Mapbox";
import { ONE_HOUR_PX } from "modules/shared/PickupDropoff/constants";
import { selectDraftId } from "modules/client/active-orders/store/orderSlice";
import PickupDropoffContainer from "modules/shared/PickupDropoff/PickupDropoffContainer";
import withFetchOne from "hocs/withFetchOne";
import { ROUTE_JOB_KIND, useRouteJobResult } from "modules/shared/route-job-result";
import { useDriverLocationByRoute } from "modules/shared/driver-location";
import { selectRouteDispatch } from "../store/slice";
import { doGetRoutesDispatch } from "../store/thunks";
import { StopSearch } from "./StopSearch";
import request from "helper/request";
import routeApi from "../store/api";
import { buildPricingEstimateBody } from "modules/client/active-orders/helper";
import { ProfileContext } from "layouts/private/ProfileContext";

import "./RouteDispatchPage.scss";

const RouteDispatchPage = ({ isAdmin }) => {
  const params = useParams();
  const historyHook = useHistory();
  const queryParams = new URLSearchParams(window.location.search);

  const dispatch = useDispatch();
  const draftIdSelector = useSelector(selectDraftId);
  const routeDispatch = useSelector(selectRouteDispatch);
  const currentDriver = useDriverLocationByRoute(routeDispatch?.id || "");
  const stopContainerHolder = useMemo(() => ({}), []);
  // Always allow optimization on dispatch page - can re-optimize existing routes
  const allowOptimize = true;

  // Get client profile to determine if organization (B2B) or individual (B2C)
  const profileCtx = useContext(ProfileContext);
  const profile = useMemo(() => profileCtx?.profile, [profileCtx]);
  const clientType = profile?.client?.clientType || "individual";

  const [oneHourPx, setOneHourPx] = useState(ONE_HOUR_PX);
  const [updating, setUpdating] = useState(false);
  const [newEstimate, setNewEstimate] = useState(null);
  const [calculatingEstimate, setCalculatingEstimate] = useState(false);

  // Map routes with routeLocations to match ConfirmRoutePage and RouteReOptimizerPage pattern
  const mappedRoutes = useMemo(() => {
    if (!routeDispatch?.routes || !routeDispatch?.locations) return [];
    return routeDispatch.routes.map((r) => ({
      ...r,
      lat: r.lat || r.startAddressLat,
      lng: r.lng || r.startAddressLng,
      routeLocations: routeDispatch.locations?.filter((s) => s.routeId === r.id)
    }));
  }, [routeDispatch?.routes, routeDispatch?.locations]);
  const updatingResult = useRouteJobResult(
    ROUTE_JOB_KIND.UPDATING,
    draftIdSelector,
    updating
  );

  useEffect(() => {
    const res = updatingResult;
    (async () => {
      if (!res) return;

      if (res?.payload?.data?.error) {
        notification.error({ massage: res?.payload?.data?.error?.message });
        setUpdating(false);
        return;
      }

      if (res.event === "route.updating.completed") {
        if (queryParams.has("from")) {
          queryParams.delete("from");
          historyHook.replace({
            search: queryParams.toString()
          });
        }
        notification.success({ message: "Updated successfully!" });
        setUpdating(false);
        await dispatch(doGetRoutesDispatch(params.id));

        // Calculate new estimate after re-optimization
        calculateNewEstimate();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatingResult]);

  const calculateNewEstimate = async () => {
    if (!routeDispatch) return;

    setCalculatingEstimate(true);
    try {
      console.log("===ROUTE DISPATCH DATA===", routeDispatch);

      // Get the first route to access serviceId and vehicleCategoryId
      const firstRoute = routeDispatch.routes?.[0] || routeDispatch;

      if (!firstRoute.serviceId) {
        notification.error({ message: "Service ID not found on route" });
        return;
      }

      if (!firstRoute.vehicleCategoryId) {
        notification.error({ message: "Vehicle Category ID not found on route" });
        return;
      }

      // Build jobs from routeLocations by grouping pickup/dropoff by jobId
      const jobsMap = {};
      (routeDispatch.routeLocations || []).forEach((rl) => {
        const jobId = rl.job?.id || rl.jobId;
        if (!jobId) return;

        if (!jobsMap[jobId]) {
          jobsMap[jobId] = {
            id: jobId,
            packages: rl.job?.packages || [],
            pickup: null,
            dropoff: null
          };
        }

        const location = rl.location;
        if (location?.type === "pickup") {
          jobsMap[jobId].pickup = {
            address: location.address || location.physicalAddress,
            lat: location.lat,
            lng: location.lng,
            start: location.start,
            end: location.end,
            duration: rl.duration || 0,
            internalOrderId: location.internalOrderId
          };
        } else if (location?.type === "dropoff") {
          jobsMap[jobId].dropoff = {
            address: location.address || location.physicalAddress,
            lat: location.lat,
            lng: location.lng,
            start: location.start,
            end: location.end,
            duration: rl.duration || 0,
            internalOrderId: location.internalOrderId
          };
        }
      });

      const jobs = Object.values(jobsMap);

      // Build a route object in the format expected by buildPricingEstimateBody
      const routeForEstimate = {
        serviceId: firstRoute.serviceId,
        vehicleCategory: { id: firstRoute.vehicleCategoryId },
        workingMins: firstRoute.workingMins,
        distance: firstRoute.distance,
        returnDistance: firstRoute.returnDistance || 0,
        returnMins: (firstRoute.returnDuration || 0) / 60,
        jobs
      };

      // Use the same pricing estimate helper as route creation. Return distance is always
      // billed by contract, subject to the Free/Short/Long tiers - returnToPickupEnabled is
      // compliance-only and never gates this charge.
      const estimatePayload = buildPricingEstimateBody(routeForEstimate, clientType);

      console.log("===ESTIMATE PAYLOAD===", estimatePayload);

      const response = await request.post("/pricing/estimate", estimatePayload);
      setNewEstimate(response);
    } catch (error) {
      console.error("Failed to calculate estimate:", error);
      notification.error({ message: "Failed to calculate new estimate" });
    } finally {
      setCalculatingEstimate(false);
    }
  };

  const handleAcceptNewEstimate = async () => {
    if (!newEstimate || !routeDispatch?.id) return;

    try {
      await routeApi.updateRouteEstimates(routeDispatch.id, {
        clientEstimate: newEstimate.client,
        driverEstimate: newEstimate.driver
      });

      notification.success({ message: "Estimate updated successfully!" });
      setNewEstimate(null);

      // Redirect to route detail page after accepting estimate
      const routesUri = isAdmin ? "/admin/routes" : "/routes";
      historyHook.push(`${routesUri}/${params.id}`);
    } catch (error) {
      console.error("Failed to update estimate:", error);
      notification.error({ message: "Failed to update estimate" });
    }
  };

  const handleConfirmRoute = () => {};

  const onStopClick = (stop) => {
    stopContainerHolder.focus?.(stop);
  };

  const handleZoomIn = () => {
    setOneHourPx(oneHourPx + oneHourPx / 10);
  };
  const handleZoomOut = () => {
    setOneHourPx(oneHourPx - oneHourPx / 10);
  };

  // Get current estimate price from routeDispatch
  const currentEstimatePrice = routeDispatch?.clientEstimate?.estimatePrice;
  const newEstimatePrice = newEstimate?.client?.estimatePrice;

  return (
    <Spin spinning={updating}>
      <div className="route-dispatch-detail">
        <PickupDropoffContainer
          holder={stopContainerHolder}
          stops={routeDispatch?.locations}
          routes={mappedRoutes}
          oneHourPx={oneHourPx}
        >
          {({
            handleUpdateAllRoutes,
            handleUpdateRoute,
            handleClickMarker,
            handleHoverMarker,
            popupData,
            handleClosePopup,
            goToCoupleStop,
            handleStopDetail,
            markerActive,
            center,
            containerStops,
            originStops,
            containerRoutes,
            mapStops,
            bounds,
            displayAll,
            driverDetail,
            setDriverDetail,
            showDriverDetail,
            setShowDriverDetail,
            groupStops,
            onDragEnd,
            onDragStart,
            handleClickHistory,
            history,
            earliestRoute,
            disabledNextButton,
            disabledPreviousButton,
            dndStatus
          }) => (
            <>
              <MapBox
                handleClosePopup={handleClosePopup}
                handleClickMarker={handleClickMarker}
                handleHoverMarker={handleHoverMarker}
                stops={mapStops}
                center={center}
                routes={containerRoutes}
                popupData={popupData}
                goToCoupleStop={goToCoupleStop}
                height={500}
                bounds={bounds}
                currentDriver={currentDriver}
                driverDetail={driverDetail}
                setDriverDetail={setDriverDetail}
                showDriverDetail={showDriverDetail}
                setShowDriverDetail={setShowDriverDetail}
                isAdmin={isAdmin}
              />
              <ChartContainer
                handleStopDetail={handleStopDetail}
                originStops={originStops}
                showConfirmButton={false}
                handleConfirmRoute={handleConfirmRoute}
                routes={containerRoutes}
                stops={containerStops}
                route={routeDispatch}
                height={500}
                order={routeDispatch}
                handleClickMarker={handleClickMarker}
                handleHoverMarker={handleHoverMarker}
                handleClosePopup={handleClosePopup}
                isShowButtonAction={false}
                setDriverDetail={setDriverDetail}
                setShowDriverDetail={setShowDriverDetail}
                handleUpdateRoute={handleUpdateRoute}
                handleUpdateAllRoutes={handleUpdateAllRoutes}
                displayAll={displayAll}
                groupStops={groupStops}
                onDragEnd={onDragEnd}
                onDragStart={onDragStart}
                handleClickHistory={handleClickHistory}
                history={history}
                earliestRoute={earliestRoute}
                disabledNextButton={disabledNextButton}
                disabledPreviousButton={disabledPreviousButton}
                dndStatus={dndStatus}
                oneHourPx={oneHourPx}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                allowOptimize={allowOptimize}
                onSetUpdating={setUpdating}
              />
              <StopSearch stops={containerStops} onStopClick={onStopClick} />
            </>
          )}
        </PickupDropoffContainer>

        {currentEstimatePrice && (
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px 30px",
              border: "1px solid #d9d9d9",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              marginTop: "20px",
              display: "flex",
              justifyContent: "center"
            }}
          >
            <div
              className="estimate-sub-container"
              style={{
                maxWidth: "500px",
                width: "100%",
                textAlign: "center"
              }}
            >
              <div style={{ marginBottom: newEstimatePrice ? "10px" : "0" }}>
                <strong>Current Estimate:</strong> ${currentEstimatePrice.toFixed(2)}
              </div>
              {calculatingEstimate && (
                <div style={{ color: "#1890ff", fontSize: "12px" }}>
                  Calculating new estimate...
                </div>
              )}
              {newEstimatePrice && (
                <div
                  style={{
                    borderTop: "1px solid #f0f0f0",
                    paddingTop: "10px",
                    marginTop: "10px"
                  }}
                >
                  <div style={{ marginBottom: "10px", color: "#52c41a" }}>
                    <strong>New Estimate:</strong> ${newEstimatePrice.toFixed(2)}
                  </div>
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleAcceptNewEstimate}
                    block
                  >
                    Accept New Estimate
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Spin>
  );
};

export default withFetchOne(RouteDispatchPage, {
  fetchData: doGetRoutesDispatch
});
